import React, { useState, useRef, useEffect } from 'react';
import { Settings, Move } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Checkbox } from '@/shared/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { MarkerAttributes, PhotoMarker } from '../types/types';

interface MarkerToolbarProps {
  visible: boolean;
  currentMarkerNumber: number;
  markerAttributes: MarkerAttributes;
  maintainAspectRatio: boolean;
  photoMarkers: PhotoMarker[];
  selectedMarkers: Set<number>;
  onMarkerNumberChange: (number: number) => void;
  onMarkerAttributesChange: (attributes: MarkerAttributes) => void;
  onMaintainAspectRatioChange: (maintain: boolean) => void;
  onResetSequence: () => void;
  onUpdateSelectedMarkers?: (updates: Partial<PhotoMarker>) => void;
}

export const MarkerToolbar: React.FC<MarkerToolbarProps> = ({
  visible,
  currentMarkerNumber,
  markerAttributes,
  maintainAspectRatio,
  photoMarkers,
  selectedMarkers,
  onMarkerNumberChange,
  onMarkerAttributesChange,
  onMaintainAspectRatioChange,
  onResetSequence,
  onUpdateSelectedMarkers,
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  // Initialize position to bottom right on mount
  useEffect(() => {
    if (visible && cardRef.current) {
      const card = cardRef.current;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const cardRect = card.getBoundingClientRect();
      
      // Position in bottom right corner
      setPosition({
        x: windowWidth - cardRect.width - 16, // 16px from right edge
        y: windowHeight - cardRect.height - 16, // 16px from bottom edge
      });
    }
  }, [visible]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  if (!visible) return null;

  const handleWidthChange = (newWidth: number) => {
    const newAttrs = { ...markerAttributes, width: newWidth };
    if (maintainAspectRatio) {
      newAttrs.height = Math.round(newWidth / markerAttributes.aspectRatio);
    }
    onMarkerAttributesChange(newAttrs);
    
    // Apply to selected markers
    if (selectedMarkers.size > 0 && onUpdateSelectedMarkers) {
      onUpdateSelectedMarkers({
        width: newWidth,
        ...(maintainAspectRatio ? { height: Math.round(newWidth / markerAttributes.aspectRatio) } : {}),
      });
    }
  };

  const handleHeightChange = (newHeight: number) => {
    const newAttrs = { ...markerAttributes, height: newHeight };
    if (maintainAspectRatio) {
      newAttrs.width = Math.round(newHeight * markerAttributes.aspectRatio);
    }
    onMarkerAttributesChange(newAttrs);
    
    // Apply to selected markers
    if (selectedMarkers.size > 0 && onUpdateSelectedMarkers) {
      onUpdateSelectedMarkers({
        height: newHeight,
        ...(maintainAspectRatio ? { width: Math.round(newHeight * markerAttributes.aspectRatio) } : {}),
      });
    }
  };

  const handleFontSizeChange = (fontSize: number) => {
    onMarkerAttributesChange({
      ...markerAttributes,
      fontSize,
    });
    
    // Apply to selected markers
    if (selectedMarkers.size > 0 && onUpdateSelectedMarkers) {
      onUpdateSelectedMarkers({ fontSize });
    }
  };

  const handleResetSequence = () => {
    const sortedMarkers = [...photoMarkers].sort((a, b) => {
      if (a.y === b.y) return a.x - b.x;
      return a.y - b.y;
    });
    const updatedMarkers = sortedMarkers.map((marker, index) => ({
      ...marker,
      number: index + 1,
    }));
    onMarkerNumberChange(updatedMarkers.length + 1);
    onResetSequence();
  };

  return (
    <Card 
      ref={cardRef}
      className="fixed w-64 z-50 shadow-lg"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      <CardHeader 
        className="pb-3 cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
      >
        <CardTitle className="text-lg flex items-center gap-2">
          <Move className="h-4 w-4 text-muted-foreground" />
          <Settings className="h-5 w-5" />
          Photo Marker Editor
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
          <Label className="text-sm">Sequence:</Label>
          <Input
            type="number"
            value={currentMarkerNumber}
            onChange={(e) => onMarkerNumberChange(parseInt(e.target.value))}
            className="w-16 h-8"
            min="1"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetSequence}
          >
            Reset
          </Button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="markerWidth">Width (px)</Label>
          <Input
            id="markerWidth"
            type="number"
            value={markerAttributes.width}
            onChange={(e) => handleWidthChange(parseInt(e.target.value))}
            min="10"
            max="500"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="markerHeight">Height (px)</Label>
          <Input
            id="markerHeight"
            type="number"
            value={markerAttributes.height}
            onChange={(e) => handleHeightChange(parseInt(e.target.value))}
            min="10"
            max="500"
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="maintainAspectRatio"
            checked={maintainAspectRatio}
            onCheckedChange={onMaintainAspectRatioChange}
          />
          <Label htmlFor="maintainAspectRatio" className="text-sm">
            Maintain Aspect Ratio
          </Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="markerFontSize">Font Size (px)</Label>
          <Input
            id="markerFontSize"
            type="number"
            value={markerAttributes.fontSize}
            onChange={(e) => handleFontSizeChange(parseInt(e.target.value))}
            min="8"
            max="48"
          />
        </div>
      </CardContent>
    </Card>
  );
}; 